import { memo } from 'react';
import cn from 'classnames';
import { VStack } from '@direct-frontend/components';
import { useDrawerDrag } from './lib/hooks/useDrawerDrag';
import { type DrawerProps } from './lib/types';
import { ANIMATION_STATES, useAnimationState } from '../../lib/animationState';
import { Overlay } from '../Overlay';
import { useDrawerContext } from './DrawerContext';
import styles from './Drawer.module.css';

type DrawerComponentProps = Pick<
    DrawerProps,
    | 'isDraggable'
    | 'children'
    | 'header'
    | 'floating'
    | 'maxSize'
    | 'closable'
    | 'dismissOnOutsideClick'
    | 'onAnimationEnd'
    | 'data-testid'
>;

export const DrawerBody = memo<DrawerComponentProps>(function Drawer({
    'data-testid': dataTestId = 'DrawerBody',
    isDraggable = true,
    children,
    header,
    floating,
    maxSize,
    closable,
    dismissOnOutsideClick,
    onAnimationEnd: onAnimationEndProp,
}) {
    const {
        refs: { overlayRef, drawerRef, contentRef, headerRef, containerRef, floatingRef },
        isFullscreen,
        isOpen,
        onClose,
    } = useDrawerContext();

    const { animationState, onAnimationEnd } = useAnimationState({
        target: drawerRef,
        isOpen,
        onAnimationEnd: onAnimationEndProp,
    });

    const { dragProps } = useDrawerDrag({
        isDraggable,
        isNegativeDraggable: !isFullscreen,
        isAnimated: animationState !== ANIMATION_STATES.NONE,
        maxSize,
    });

    return (
        <Overlay
            data-testid={dataTestId}
            position="center"
            preventScroll={false}
            ref={overlayRef}
            isOpen={isOpen}
            onClose={closable ? onClose : undefined}
            className={styles.overlay}
            dismissOnOutsideClick={dismissOnOutsideClick}
            containFocus={false}
            {...(isDraggable ? dragProps : {})}
        >
            <div
                data-testid={`${dataTestId}.Drawer`}
                ref={drawerRef}
                className={cn(styles.drawer, styles[animationState], {
                    [styles.fullscreen]: isFullscreen,
                    [styles.withoutHeader]: !header,
                })}
                onAnimationEnd={onAnimationEnd}
            >
                <VStack data-testid={`${dataTestId}.Container`} ref={containerRef} className={styles.container}>
                    {isDraggable && <div data-testid={`${dataTestId}.Pull`} className={styles.containerPull} />}

                    {floating && (
                        <div data-testid={`${dataTestId}.Floating`} ref={floatingRef} className={styles.floating}>
                            {floating}
                        </div>
                    )}

                    {header && (
                        <div data-testid={`${dataTestId}.Header`} ref={headerRef} className={styles.header}>
                            {header}
                        </div>
                    )}
                    <div data-testid={`${dataTestId}.Content`} ref={contentRef} className={styles.content}>
                        {children}
                    </div>
                </VStack>
            </div>
        </Overlay>
    );
});
